/**
 * 🧠 MOTOR MUSICAL — Navegador de Brillo Modal (Bordona Edition)
 *
 * Lógica basada en la Matriz Numérica del Círculo de Quintas:
 *   Armadura = Quinta(Tónica) + Offset(Modo)
 *
 * Adaptado a la paleta y tipos de Bordona.
 */

import { type NoteName, type NeckPosition, noteToSemitones, semitonesToNote, getNoteAtPosition, shouldUseFlats, OPEN_STRINGS } from "./guitarEngine";
import type { FretboardPattern, FingeredNote } from "./fingeringEngine";

export interface ModoDefinition {
  id: string;
  nombre: string;
  brillo: number;
  offset: number;
  estructura: number[]; // semitonos desde tónica
  caracteristica: number | null; // semitono de la nota característica
  gradoPortal: string;
  nombrePortal: string;
  desc: string;
}

export const MODOS: ModoDefinition[] = [
  { id: "locrio",     nombre: "Locrio",     brillo: -5, offset: -5, estructura: [0, 1, 3, 5, 6, 8, 10],  caracteristica: 6,  gradoPortal: "♭II",  nombrePortal: "segunda menor", desc: "Oscuro, inestable, tritón angular. El modo más frío." },
  { id: "frigio",     nombre: "Frigio",     brillo: -4, offset: -4, estructura: [0, 1, 3, 5, 7, 8, 10],  caracteristica: 1,  gradoPortal: "♭VI",  nombrePortal: "sexta menor",   desc: "Exótico, tensión española. El semitono ♭2 lo define." },
  { id: "eolio",      nombre: "Eolio",      brillo: -3, offset: -3, estructura: [0, 2, 3, 5, 7, 8, 10],  caracteristica: 8,  gradoPortal: "♭III", nombrePortal: "tercera menor", desc: "Melancólico, íntimo. La menor natural pura." },
  { id: "dorico",     nombre: "Dórico",     brillo: -2, offset: -2, estructura: [0, 2, 3, 5, 7, 9, 10],  caracteristica: 9,  gradoPortal: "VII",  nombrePortal: "séptima",       desc: "Brillante pero sombrío. La 6ª mayor le da luz." },
  { id: "mixolidio",  nombre: "Mixolidio",  brillo: -1, offset: -1, estructura: [0, 2, 4, 5, 7, 9, 10],  caracteristica: 10, gradoPortal: "IV",   nombrePortal: "cuarta justa",  desc: "Relajado, bluesy. La ♭7 suelta la tensión." },
  { id: "jonico",     nombre: "Jónico",     brillo:  0, offset:  0, estructura: [0, 2, 4, 5, 7, 9, 11],  caracteristica: null, gradoPortal: "I",  nombrePortal: "tónica",        desc: "Estable, feliz. La referencia mayor perfecta." },
  { id: "lidio",      nombre: "Lidio",      brillo: +1, offset: +1, estructura: [0, 2, 4, 6, 7, 9, 11],  caracteristica: 6,  gradoPortal: "V",    nombrePortal: "quinta justa",  desc: "Etéreo, flotante. El #4 abre el cielo." },
];

export const NOTAS: NoteName[] = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

const QUINTAS = [0, 7, 2, 9, 4, 11, 6, 1, 8, 3, 10, 5];

export function nombreNota(index: number, usarBemol: boolean = false): NoteName {
  const i = ((index % 12) + 12) % 12;
  if (usarBemol) {
    const flat: NoteName[] = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
    return flat[i];
  }
  return NOTAS[i];
}

function quintaIndex(nota: NoteName): number {
  const idx = noteToSemitones(nota);
  return QUINTAS.indexOf(idx);
}

export function calcularArmadura(tonica: NoteName, modoId: string): number {
  const modo = MODOS.find((m) => m.id === modoId);
  if (!modo) return 0;
  const q = quintaIndex(tonica);
  if (q === -1) return 0;
  return q + modo.offset;
}

export function notasDelModo(tonica: NoteName, modoId: string): NoteName[] {
  const modo = MODOS.find((m) => m.id === modoId);
  if (!modo) return [];
  const raiz = noteToSemitones(tonica);
  return modo.estructura.map((st) => nombreNota(raiz + st, true));
}

export interface PortalInfo {
  nota: NoteName;
  grado: string;
  nombreGrado: string;
  instruccion: string;
}

export function calcularPortal(tonica: NoteName, modoId: string): PortalInfo | null {
  const modo = MODOS.find((m) => m.id === modoId);
  if (!modo) return null;
  const raiz = noteToSemitones(tonica);

  const semitonosPortal: Record<string, number> = {
    jonico: 0,
    dorico: -2,
    frigio: -4,
    eolio: 3,
    mixolidio: 5,
    lidio: 7,
    locrio: 1,
  };

  const desplazamiento = semitonosPortal[modoId] ?? 0;
  const portalIndex = ((raiz + desplazamiento) % 12 + 12) % 12;
  const portalNota = nombreNota(portalIndex, true);

  return {
    nota: portalNota,
    grado: modo.gradoPortal,
    nombreGrado: modo.nombrePortal,
    instruccion: `Toca todas las notas de ${portalNota} Mayor, pero haz que tu bajo sea ${tonica}`,
  };
}

export interface AcordeInfo {
  siglas: string;
  notas: NoteName[];
}

export function acordeTonica(tonica: NoteName, modoId: string): AcordeInfo | null {
  const notas = notasDelModo(tonica, modoId);
  if (notas.length === 0) return null;

  const mapa: Record<string, string> = {
    jonico: "Maj7",
    dorico: "m7",
    frigio: "m7",
    eolio: "m7",
    mixolidio: "7",
    lidio: "Maj7",
    locrio: "m7♭5",
  };

  return {
    siglas: mapa[modoId] || "",
    notas: [notas[0], notas[2], notas[4], notas[6]],
  };
}

export interface Tension {
  grado: string;
  nota: NoteName;
}

export function tensionesModo(tonica: NoteName, modoId: string): Tension[] {
  const notas = notasDelModo(tonica, modoId);
  if (notas.length === 0) return [];
  return [
    { grado: "9", nota: notas[1] },
    { grado: "11", nota: notas[3] },
    { grado: "13", nota: notas[5] },
  ];
}

export interface AcordeDisparador {
  nombre: string;
  desc: string;
}

export function acordesDisparadores(modoId: string, tonica: NoteName): AcordeDisparador[] {
  const notas = notasDelModo(tonica, modoId);
  if (notas.length === 0) return [];

  const mapa: Record<string, AcordeDisparador[]> = {
    locrio: [
      { nombre: `${notas[0]}m7♭5`, desc: "Semidisminuido — tensión angular" },
      { nombre: `${notas[1]}Maj7`,  desc: "♭II Mayor — resolución oscura" },
    ],
    frigio: [
      { nombre: `${notas[0]}m7`,    desc: "Tónica menor — sonido español" },
      { nombre: `${nombreNota(noteToSemitones(notas[0]) - 2, true)}Maj7`, desc: "♭II Mayor — tensión frigia" },
    ],
    eolio: [
      { nombre: `${notas[0]}m7`,    desc: "Tónica menor — melancolía pura" },
      { nombre: `${notas[3]}m7`,    desc: "IVm7 — color modal suave" },
    ],
    dorico: [
      { nombre: `${notas[0]}m7`,    desc: "Tónica menor — brillo modal" },
      { nombre: `${notas[3]}7`,     desc: "IV7 — groove dórico" },
    ],
    mixolidio: [
      { nombre: `${notas[0]}7`,     desc: "Dominante — tensión relajada" },
      { nombre: `${notas[3]}m7`,    desc: "IVm7 — sabor bluesy" },
    ],
    jonico: [
      { nombre: `${notas[0]}Maj7`,  desc: "Mayor 7 — estabilidad" },
      { nombre: `${notas[4]}m7`,    desc: "VIm7 — relativa menor" },
    ],
    lidio: [
      { nombre: `${notas[0]}Maj7`,  desc: "Maj7(#11) — brillo etéreo" },
      { nombre: `${notas[1]}m7`,    desc: "IIm7 — flotación lidia" },
    ],
  };

  return mapa[modoId] || [];
}

/** Genera NeckPositions para el GuitarNeck de Bordona */
export function buildModoPositions(tonica: NoteName, modoId: string, maxFret: number = 15): NeckPosition[] {
  const modo = MODOS.find((m) => m.id === modoId);
  if (!modo) return [];

  const raizSemi = noteToSemitones(tonica);
  const notasSet = new Set(modo.estructura.map((st) => ((raizSemi + st) % 12 + 12) % 12));
  const caracteristicaSemi = modo.caracteristica !== null ? ((raizSemi + modo.caracteristica) % 12 + 12) % 12 : null;

  const positions: NeckPosition[] = [];

  for (let stringNum = 1; stringNum <= 6; stringNum++) {
    for (let fret = 0; fret <= maxFret; fret++) {
      const noteData = getNoteAtPosition(stringNum, fret);
      const noteSemi = noteToSemitones(noteData.note);

      if (!notasSet.has(noteSemi)) continue;

      let highlight: NeckPosition["highlight"] = "scale";
      if (noteSemi === raizSemi) highlight = "root";
      else if (noteSemi === caracteristicaSemi) highlight = "third"; // reutilizamos el color de "third" para característica

      positions.push({
        string: stringNum,
        fret,
        note: noteData.note,
        degree: undefined,
        highlight,
      });
    }
  }

  return positions;
}

/** Grados romanos de la escala del modo */
export function gradosDelModo(modoId: string): string[] {
  const mapa: Record<string, string[]> = {
    jonico:     ["I", "II", "III", "IV", "V", "VI", "VII"],
    dorico:     ["i", "II", "♭III", "IV", "v", "vi", "♭VII"],
    frigio:     ["i", "♭II", "♭III", "iv", "v", "♭VI", "♭VII"],
    eolio:      ["i", "II", "♭III", "iv", "v", "♭VI", "♭VII"],
    mixolidio:  ["I", "II", "iii", "iv", "v", "VI", "♭VII"],
    lidio:      ["I", "II", "III", "♯IV", "V", "VI", "vii"],
    locrio:     ["i", "♭II", "♭III", "iv", "♭V", "♭VI", "♭VII"],
  };
  return mapa[modoId] || [];
}

// ─── CAGED para Modos ───

const MODO_DEGREE_MAP: Record<string, string[]> = {
  jonico:     ["R", "2", "3", "4", "5", "6", "7"],
  dorico:     ["R", "2", "b3", "4", "5", "6", "b7"],
  frigio:     ["R", "b2", "b3", "4", "5", "b6", "b7"],
  eolio:      ["R", "2", "b3", "4", "5", "b6", "b7"],
  mixolidio:  ["R", "2", "3", "4", "5", "6", "b7"],
  lidio:      ["R", "2", "3", "#4", "5", "6", "7"],
  locrio:     ["R", "b2", "b3", "4", "b5", "b6", "b7"],
};

/** Grados de escala en mástil (R, 2, b3, …) para leyenda y reproducción sandbox. */
export function gradosEscalaModo(modoId: string): string[] {
  return MODO_DEGREE_MAP[modoId] ?? MODO_DEGREE_MAP.jonico;
}

function assignModoFinger(fret: number, position: number): number {
  const offset = fret - position;
  if (offset <= -1) return 1;
  if (offset === 0) return 1;
  if (offset === 1) return 2;
  if (offset === 2) return 3;
  if (offset >= 3) return 4;
  return 0;
}

function calculateModoComfort(notes: FingeredNote[], position: number): number {
  let comfort = 100;
  const pinkyCount = notes.filter((n) => n.finger === 4).length;
  comfort -= pinkyCount * 5;
  const indexStretchBack = notes.filter((n) => n.finger === 1 && n.fret < position).length;
  comfort -= indexStretchBack * 4;
  const avgFret = notes.reduce((a, n) => a + n.fret, 0) / notes.length;
  if (avgFret > 12) comfort -= 10;
  else if (avgFret > 9) comfort -= 5;
  if (avgFret <= 5) comfort += 8;
  const uniqueDegrees = new Set(notes.map((n) => n.degree)).size;
  if (uniqueDegrees < 5) comfort -= (5 - uniqueDegrees) * 10;
  return Math.max(0, comfort);
}

/**
 * Genera cajas CAGED para un modo dado (misma lógica de ventana deslizante que escalas)
 */
export function generateModoFingerings(
  tonica: NoteName,
  modoId: string,
  maxFret: number = 15
): FretboardPattern[] {
  const modo = MODOS.find((m) => m.id === modoId);
  if (!modo) return [];

  const rootSemi = noteToSemitones(tonica);
  const useFlats = shouldUseFlats(tonica, modoId === "jonico" || modoId === "lidio" || modoId === "mixolidio" ? "major" : "minor");
  const degreeNames = MODO_DEGREE_MAP[modoId] || MODO_DEGREE_MAP.jonico;

  const scaleDegrees = modo.estructura.map((interval, idx) => ({
    semitone: (rootSemi + interval) % 12,
    degree: degreeNames[idx] || String(idx + 1),
  }));

  const results: FretboardPattern[] = [];

  for (let pos = 0; pos <= maxFret - 2; pos++) {
    const windowStart = Math.max(0, pos);
    const windowEnd = Math.min(maxFret, pos + 4);

    const allNotes: FingeredNote[] = [];
    const degreesInPattern = new Set<string>();

    for (let s = 6; s >= 1; s--) {
      const stringNotes: FingeredNote[] = [];
      for (let f = windowStart; f <= windowEnd; f++) {
        const posInfo = getNoteAtPosition(s, f);
        const posSemi = noteToSemitones(posInfo.note);
        const matched = scaleDegrees.find((sd) => sd.semitone === posSemi);
        if (matched) {
          const isOpen = f === 0 && posInfo.note === OPEN_STRINGS[6 - s].note;
          const finger = isOpen ? 0 : assignModoFinger(f, pos);
          stringNotes.push({
            string: s,
            fret: f,
            note: semitonesToNote(posSemi, useFlats),
            finger,
            degree: matched.degree,
          });
        }
      }
      const limited = stringNotes.slice(0, 3);
      for (const n of limited) {
        allNotes.push(n);
        degreesInPattern.add(n.degree);
      }
    }

    if (allNotes.length === 0) continue;

    const frets = allNotes.map((n) => n.fret);
    const minFretUsed = Math.min(...frets);
    const maxFretUsed = Math.max(...frets);
    const span = maxFretUsed - minFretUsed;

    if (span > 5) continue;

    const stringsUsed = [...new Set(allNotes.map((n) => n.string))].sort((a, b) => b - a);
    let consecutiveStrings = 1;
    let maxConsecutive = 1;
    for (let i = 1; i < stringsUsed.length; i++) {
      if (stringsUsed[i - 1] - stringsUsed[i] === 1) {
        consecutiveStrings++;
        maxConsecutive = Math.max(maxConsecutive, consecutiveStrings);
      } else {
        consecutiveStrings = 1;
      }
    }
    if (maxConsecutive < 4 && stringsUsed.length < 4) continue;

    let validStrings = true;
    for (let i = 1; i < stringsUsed.length; i++) {
      if (stringsUsed[i - 1] - stringsUsed[i] > 2) {
        validStrings = false;
        break;
      }
    }
    if (!validStrings) continue;

    const comfort = calculateModoComfort(allNotes, pos);
    const sorted = [...allNotes].sort((a, b) => b.string - a.string);

    results.push({
      position: pos,
      notes: sorted,
      span,
      comfortScore: comfort,
      stringRange: [Math.max(...stringsUsed), Math.min(...stringsUsed)] as [number, number],
      coverage: degreesInPattern.size,
    });
  }

  return results.sort((a, b) => {
    if (b.comfortScore !== a.comfortScore) return b.comfortScore - a.comfortScore;
    return a.position - b.position;
  });
}
